import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LegalBasis } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function LegalBasisPage() {
  const { persona } = useAuth();
  const canSee = persona?.roles.includes('DICT_POLICY_ADMIN') ?? false;
  const [activeOnly, setActiveOnly] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [retiring, setRetiring] = useState<LegalBasis | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (activeOnly) p.set('status', 'ACTIVE');
    return `/api/v1/consenthub/legal-basis?${p}`;
  }, [activeOnly]);
  const listQ = useApiQuery<{ items: LegalBasis[] }>(canSee ? query : null);

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Legal basis matrix" subtitle="Statutory fallback for MDA-to-MDA data shares that don't need citizen consent" />
        <EmptyState title="No access" hint="This screen needs the DICT_POLICY_ADMIN role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Legal basis matrix"
        subtitle="Statutory fallback for MDA-to-MDA data shares that don't need citizen consent — one row per (Sey-X service, consumer MDA) pair"
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Upsert row
          </button>
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <label className="mb-4 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
        Active only (hide retired)
      </label>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No legal-basis rows yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Sey-X service</th>
                <th className="table-head">Consumer MDA</th>
                <th className="table-head">Statute</th>
                <th className="table-head">Next review</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{row.seyxService}</td>
                  <td className="table-cell text-xs">{row.consumerMda}</td>
                  <td className="table-cell text-xs text-slate-500">{row.statuteRef}</td>
                  <td className="table-cell text-slate-500">{formatDate(row.reviewDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={row.retiredAt ? 'RETIRED' : 'ACTIVE'} />
                  </td>
                  <td className="table-cell text-right">
                    {!row.retiredAt ? (
                      <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setRetiring(row)}>
                        Retire
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <UpsertModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {retiring ? (
        <RetireModal
          row={retiring}
          onClose={() => setRetiring(null)}
          onDone={() => {
            setRetiring(null);
            listQ.reload();
          }}
          onError={setActionError}
        />
      ) : null}
    </div>
  );
}

function UpsertModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ seyxService: '', consumerMda: '', statuteRef: '', reviewDate: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/consenthub/legal-basis', form);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Upsert legal-basis row" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Posting an existing (service, MDA) pair updates it and clears any prior retirement.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sey-X service code">
            <input
              className="field-input"
              value={form.seyxService}
              onChange={(e) => setForm({ ...form, seyxService: e.target.value })}
              placeholder="e.g. docvault:verify"
            />
          </Field>
          <Field label="Consumer MDA">
            <input
              className="field-input"
              value={form.consumerMda}
              onChange={(e) => setForm({ ...form, consumerMda: e.target.value })}
              placeholder="e.g. SRC"
            />
          </Field>
        </div>
        <Field label="Statute reference">
          <input
            className="field-input"
            value={form.statuteRef}
            onChange={(e) => setForm({ ...form, statuteRef: e.target.value })}
            placeholder="e.g. Revenue Administration Act 2009, s.42"
          />
        </Field>
        <Field label="Next policy review date">
          <input className="field-input" type="date" value={form.reviewDate} onChange={(e) => setForm({ ...form, reviewDate: e.target.value })} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !form.seyxService || !form.consumerMda || !form.statuteRef || !form.reviewDate}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RetireModal({
  row,
  onClose,
  onDone,
  onError,
}: {
  row: LegalBasis;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/v1/consenthub/legal-basis/${row.id}/retire`, { reason });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Retire failed');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Retire ${row.seyxService} → ${row.consumerMda}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Retiring…' : 'Retire'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
